import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/api/client";
import type { components } from "@/api/schema";
import { keys, RequestError, type StepDoc } from "@/app/queries";

/* Вкладка «домашка» A1: загрузка файлов шага (presign → PUT → register) и
   задание шага (манифест из ноутбука-шаблона). Хуков в queries.ts нет —
   локальные помощники конструктора, как api.ts. */

export type Assignment = components["schemas"]["Assignment"];
export type FileRole = "шаблон" | "эталон" | "описание" | "данные";

export const FILE_ROLES: FileRole[] = ["шаблон", "эталон", "описание", "данные"];
export const ACCEPT = ".ipynb,.md,.csv,.py,.zip";
export const EXTENSIONS = ["ipynb", "md", "csv", "py", "zip"];
/** Пределы §8 спеки — зеркало notebook.MaxFileBytes / MaxStepFilesBytes. */
export const MAX_FILE_BYTES = 5 * 1024 * 1024;
export const MAX_STEP_BYTES = 10 * 1024 * 1024;

export const assignmentKey = (stepId: string) => ["admin", "assignment", stepId] as const;

function ext(name: string): string {
  const i = name.lastIndexOf(".");
  return i < 0 ? "" : name.slice(i + 1).toLowerCase();
}

export function acceptedExt(name: string): boolean {
  return EXTENSIONS.includes(ext(name));
}

/** Роль по имени файла: ноутбук — шаблон (эталон, если в имени solution/эталон),
 *  csv и zip — данные, md — описание, py — эталон (скрытые тесты раннера: по
 *  умолчанию прячем, открыть студентам можно сменой роли). */
export function defaultRole(name: string): FileRole {
  const lower = name.toLowerCase();
  switch (ext(name)) {
    case "ipynb":
      return lower.includes("solution") || lower.includes("эталон") ? "эталон" : "шаблон";
    case "csv":
    case "zip":
      return "данные";
    case "md":
      return "описание";
    case "py":
      return "эталон";
    default:
      return "описание";
  }
}

/** Content-Type загрузки: presign подписывает именно его, PUT обязан совпасть.
 *  Берём по расширению, а не file.type — у .ipynb браузер типа не знает. */
export function contentTypeFor(name: string): string {
  switch (ext(name)) {
    case "ipynb":
      return "application/x-ipynb+json";
    case "csv":
      return "text/csv";
    case "md":
      return "text/markdown";
    case "py":
      return "text/x-python";
    case "zip":
      return "application/zip";
    default:
      return "application/octet-stream";
  }
}

function fail(status: number, error: unknown, fallback: string): never {
  const e = error as { error?: string; message?: string } | undefined;
  throw new RequestError(status, e?.error ?? "unknown", e?.message ?? fallback);
}

/** presign → PUT байтов в хранилище → регистрация в шаге. Возвращает черновик шага с файлом. */
export async function uploadStepFile(stepId: string, file: File, role: FileRole): Promise<StepDoc> {
  const contentType = contentTypeFor(file.name);
  const pre = await api.POST("/admin/files/presign", { body: { name: file.name, size: file.size, content_type: contentType, role } });
  if (!pre.data) fail(pre.response.status, pre.error, "Сервер не выдал ссылку для загрузки.");

  let put: Response;
  try {
    put = await fetch(pre.data.upload_url, { method: "PUT", headers: { "Content-Type": contentType }, body: file });
  } catch {
    throw new RequestError(0, "network", "Хранилище не ответило — проверьте связь и повторите. В шаг ничего не добавлено.");
  }
  if (!put.ok) throw new RequestError(put.status, "storage", `Хранилище не приняло файл (код ${put.status}). В шаг ничего не добавлено — повторите загрузку.`);

  const reg = await api.POST("/admin/steps/{stepId}/files", { params: { path: { stepId } }, body: { key: pre.data.key, name: file.name, role } });
  if (!reg.data) fail(reg.response.status, reg.error, "Файл загружен, но шаг его не принял — повторите.");
  return reg.data;
}

export function useUploadStepFile(stepId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ file, role }: { file: File; role: FileRole }) => uploadStepFile(stepId, file, role),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.draft(stepId) });
      void qc.invalidateQueries({ queryKey: assignmentKey(stepId) });
    },
  });
}

/** Задание шага. null — у шага ещё нет ноутбука-шаблона (404 not_found). */
export function useStepAssignment(stepId: string) {
  return useQuery({
    queryKey: assignmentKey(stepId),
    queryFn: async (): Promise<Assignment | null> => {
      const { data, error, response } = await api.GET("/admin/steps/{stepId}/assignment", { params: { path: { stepId } } });
      if (data) return data;
      if (response.status === 404) return null;
      fail(response.status, error, "Не удалось получить задание шага.");
    },
    retry: (n, err) => !(err instanceof RequestError && err.status < 500 && err.status > 0) && n < 2,
  });
}

export function useUpdateAssignment(stepId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { threshold?: number; timeout_s?: number }) => {
      const { data, error, response } = await api.PUT("/admin/steps/{stepId}/assignment", { params: { path: { stepId } }, body });
      if (!data) fail(response.status, error, "Порог не сохранился.");
      return data;
    },
    onSuccess: (a) => qc.setQueryData(assignmentKey(stepId), a),
  });
}
